/**
 * Phép tính thuần của form cài đặt dựng từ danh mục tham số (ADR-0059 mục 1, 8).
 *
 * Bốn việc: giải giá trị hiệu lực qua ba tầng (mock dùng, giống
 * `SettingsResolver` của platform-common), tính thay đổi giữa bản nháp và bản đã
 * lưu, kiểm biên phía giao diện, và nhận ra thay đổi có thể khoá người dùng.
 *
 * Luật của thư mục `nghiep-vu/`: không đọc đồng hồ (nhận `bayGio`), không gọi
 * HTTP, không dựng chuỗi hiển thị — trả **mã** để view tự dịch.
 */
import { chuanHoaCidr, laTatCaDiaChi } from './cidr.ts';
import { docDanhSachIp } from './danh-sach-ip.ts';
import { kiemDanhSachQuyTac } from './quy-tac-canh-bao.ts';
import type {
  BanNhap,
  BienHieuLuc,
  DongCidr,
  MaGoi,
  MucCaiDat,
  NguonGiaTri,
  PhamViCaiDat,
  SettingDefinition,
  TuyenThongBao,
  VaiTroSua,
} from './types.ts';

/** So sánh sâu cho giá trị JSON (số, chuỗi, mảng, object phẳng). */
export function bangNhau(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => bangNhau(x, b[i]));
  }
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  return (
    ka.length === kb.length &&
    ka.every((k) => bangNhau((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
  );
}

// ---------------------------------------------------------------------------
// Biên và giá trị hiệu lực
// ---------------------------------------------------------------------------

export interface BienTinh extends BienHieuLuc {
  /** Trần đến từ `max_by_plan` của gói hiện tại — view thêm "theo gói hiện tại". */
  theo_goi: boolean;
}

/** Biên hiệu lực của một tham số với gói đang xét. */
export function bienHieuLuc(def: SettingDefinition, goi: MaGoi | null): BienTinh {
  const b = def.bounds ?? {};
  let max = b.max ?? null;
  let theoGoi = false;
  const tranGoi = goi ? b.max_by_plan?.[goi] : undefined;
  if (tranGoi !== undefined && tranGoi !== null) {
    max = max === null ? tranGoi : Math.min(max, tranGoi);
    theoGoi = true;
  }
  return {
    min: b.min ?? null,
    max,
    max_items: b.max_items ?? null,
    max_future_minutes: b.max_future_minutes ?? null,
    item_pattern: b.item_pattern ?? null,
    forbid_catch_all: b.forbid_catch_all ?? null,
    fields: b.fields ?? null,
    theo_goi: theoGoi,
  };
}

export interface DauVaoGiai {
  danhMuc: readonly SettingDefinition[];
  nenTang: Record<string, unknown>;
  goi: Record<string, unknown>;
  tenant: Record<string, unknown>;
  phamVi: PhamViCaiDat;
  maGoi: MaGoi | null;
  vaiTro: VaiTroSua;
}

/**
 * Giải danh mục qua ba tầng ra danh sách dòng của `SecurityPolicyView`.
 *
 * Tầng thấp nhất có đặt thắng. `inherited_*` là kết quả **khi bỏ** tầng đang
 * xem — đúng thứ người dùng nhận lại khi bấm "Về mặc định". Màn tenant nhận cả
 * tham số chỉ có ở tầng nền tảng (ở dạng chỉ đọc) để hiện chính sách mật khẩu.
 */
export function giaiCaiDat(v: DauVaoGiai): MucCaiDat[] {
  return epMfaKhiEmailNgoaiDomain(giaiTungKhoa(v));
}

/**
 * Như `SettingsResolver` (ADR-0099): tắt `auth.chi_email_domain_xac_minh` thì MFA hiệu lực bị ép bật, không sửa
 * được, đánh dấu `forced_by`.
 */
export function epMfaKhiEmailNgoaiDomain(ds: MucCaiDat[]): MucCaiDat[] {
  const email = ds.find((m) => m.key === 'auth.chi_email_domain_xac_minh');
  if (email?.value !== false) return ds;
  return ds.map((m) =>
    m.key === 'auth.mfa_required'
      ? { ...m, value: true, editable: false, forced_by: 'auth.chi_email_domain_xac_minh' }
      : m,
  );
}

function giaiTungKhoa(v: DauVaoGiai): MucCaiDat[] {
  const tang: Array<{ nguon: NguonGiaTri; giaTri: Record<string, unknown>; phamVi: PhamViCaiDat }> = [
    { nguon: 'PLATFORM', giaTri: v.nenTang, phamVi: 'PLATFORM' },
  ];
  if (v.phamVi !== 'PLATFORM') tang.push({ nguon: 'PLAN', giaTri: v.goi, phamVi: 'PLAN' });
  if (v.phamVi === 'TENANT') tang.push({ nguon: 'TENANT', giaTri: v.tenant, phamVi: 'TENANT' });

  return v.danhMuc
    .filter((d) => v.phamVi === 'TENANT' || d.scopes.includes(v.phamVi))
    .map((d) => {
      let value: unknown = d.default;
      let source: NguonGiaTri = 'DEFAULT';
      let inheritedValue = value;
      let inheritedSource: NguonGiaTri = 'DEFAULT';

      for (const t of tang) {
        if (!d.scopes.includes(t.phamVi) || !(d.key in t.giaTri)) continue;
        if (t.phamVi !== v.phamVi) {
          inheritedValue = t.giaTri[d.key];
          inheritedSource = t.nguon;
        }
        value = t.giaTri[d.key];
        source = t.nguon;
      }

      const bien = bienHieuLuc(d, v.maGoi);
      // Hạ về trần gói nhưng giữ nguồn: gói hạ trần sau khi tầng trên đã đặt
      // cao hơn thì giá trị hiệu lực là trần, còn người đặt vẫn là tầng đó.
      const tran = (x: unknown) =>
        typeof x === 'number' && bien.max !== null && bien.max !== undefined && x > bien.max ? bien.max : x;
      value = tran(value);
      inheritedValue = tran(inheritedValue);
      const { theo_goi: _theoGoi, ...bounds } = bien;
      // Mức sàn kế thừa (ADR-0078), như `SettingsResolver`: biên phía chặt lấy
      // giá trị kế thừa; tenant đặt lỏng hơn sàn thì hiệu lực là sàn.
      if (laSanKeThua(d, v.phamVi) && typeof inheritedValue === 'number') {
        const san = inheritedValue;
        if (d.direction === 'HIGHER_IS_STRICTER') {
          if (bounds.min === null || bounds.min === undefined || bounds.min < san) bounds.min = san;
          if (source === 'TENANT' && typeof value === 'number' && value < san) {
            value = san;
            source = inheritedSource;
          }
        } else if (d.direction === 'LOWER_IS_STRICTER') {
          if (bounds.max === null || bounds.max === undefined || bounds.max > san) bounds.max = san;
          if (source === 'TENANT' && typeof value === 'number' && value > san) {
            value = san;
            source = inheritedSource;
          }
        }
      }
      const editable =
        d.scopes.includes(v.phamVi) && (d.editable_by[v.phamVi] ?? []).includes(v.vaiTro);

      return {
        key: d.key,
        value,
        source,
        inherited_value: inheritedValue,
        inherited_source: inheritedSource,
        editable,
        bounds,
      };
    });
}

/**
 * Giá trị kế thừa là mức sàn của dòng này ở tầng đang xem (ADR-0078): chỉ ở
 * tầng tenant, chỉ với khoá danh mục khai `inherited_is_floor`.
 */
export function laSanKeThua(def: SettingDefinition, phamVi: PhamViCaiDat): boolean {
  return phamVi === 'TENANT' && def.inherited_is_floor === true;
}

// ---------------------------------------------------------------------------
// Thay đổi giữa bản nháp và bản đã lưu
// ---------------------------------------------------------------------------

export interface ThayDoiNhap {
  key: string;
  truoc: unknown;
  /** Giá trị sẽ có hiệu lực sau khi lưu (đã giải kế thừa nếu là "Về mặc định"). */
  sau: unknown;
  /** Thay đổi là bỏ giá trị ở tầng này (gửi `null`). */
  ve_ke_thua: boolean;
}

/**
 * Danh sách thay đổi thật sự sẽ gửi đi.
 *
 * Bỏ qua: khoá không còn trong chính sách, giá trị nháp trùng giá trị đang có,
 * và "Về mặc định" trên một dòng vốn đang kế thừa — gửi những thứ đó chỉ làm
 * tăng phiên bản mà không đổi gì, và nút lưu đếm sai.
 */
export function tinhThayDoi(
  muc: readonly MucCaiDat[],
  nhap: BanNhap,
  phamVi: PhamViCaiDat,
): ThayDoiNhap[] {
  const ra: ThayDoiNhap[] = [];
  for (const m of muc) {
    if (!(m.key in nhap)) continue;
    const moi = nhap[m.key];
    if (moi === null) {
      if (m.source !== phamVi) continue;
      ra.push({ key: m.key, truoc: m.value, sau: m.inherited_value, ve_ke_thua: true });
    } else if (!bangNhau(moi, m.value)) {
      ra.push({ key: m.key, truoc: m.value, sau: moi, ve_ke_thua: false });
    }
  }
  return ra;
}

/**
 * Khoá đang báo lỗi **và** sẽ được gửi đi — chỉ những khoá này chặn nút lưu.
 *
 * Bản nháp có thể giữ một khoá mà giá trị trùng bản đã lưu (gõ rồi xoá về như cũ): khoá đó không gửi đi, nên lỗi của
 * nó — kể cả lỗi của chính giá trị đã lưu mà phép kiểm phía giao diện không chấp nhận — không được chặn lưu những
 * thay đổi khác, và thanh lưu không được báo "Sửa 1 ô đang báo lỗi" khi người dùng không sửa ô nào.
 */
export function khoaLoiChanLuu(loi: Readonly<Record<string, unknown>>, thayDoi: readonly ThayDoiNhap[]): string[] {
  return thayDoi.filter((d) => d.key in loi).map((d) => d.key);
}

/** Thân `settings` của `PUT`: khoá → giá trị, `null` là về kế thừa. */
export function thanGhi(thayDoi: readonly ThayDoiNhap[]): Record<string, unknown> {
  return Object.fromEntries(thayDoi.map((d) => [d.key, d.ve_ke_thua ? null : d.sau]));
}

/** Giá trị đang hiện trên form của một dòng: nháp nếu có, không thì bản đã lưu. */
export function giaTriHienTai(m: MucCaiDat, nhap: BanNhap): unknown {
  if (!(m.key in nhap)) return m.value;
  const moi = nhap[m.key];
  return moi === null ? m.inherited_value : moi;
}

// ---------------------------------------------------------------------------
// Nguồn giá trị
// ---------------------------------------------------------------------------

export type LoaiNguon =
  | 'mac_dinh'
  | 'nen_tang'
  | 'goi'
  | 'tenant'
  /** Đang sửa, chưa lưu. */
  | 'dang_sua'
  /** Đã bấm "Về mặc định", chưa lưu: sẽ lấy theo `nguon_sau`. */
  | 've_ke_thua';

export interface MoTaNguon {
  loai: LoaiNguon;
  goi: MaGoi | null;
  /** Với `ve_ke_thua`: tầng sẽ quyết giá trị sau khi lưu. */
  nguon_sau?: LoaiNguon;
}

const LOAI_THEO_NGUON: Record<NguonGiaTri, LoaiNguon> = {
  DEFAULT: 'mac_dinh',
  PLATFORM: 'nen_tang',
  PLAN: 'goi',
  TENANT: 'tenant',
};

/** Nguồn hiện cạnh một dòng: "Theo gói Professional", "Tenant đặt", "Mặc định"… */
export function moTaNguon(m: MucCaiDat, nhap: BanNhap, maGoi: MaGoi | null): MoTaNguon {
  if (m.key in nhap) {
    if (nhap[m.key] === null) {
      return { loai: 've_ke_thua', goi: maGoi, nguon_sau: LOAI_THEO_NGUON[m.inherited_source] };
    }
    if (!bangNhau(nhap[m.key], m.value)) return { loai: 'dang_sua', goi: maGoi };
  }
  return { loai: LOAI_THEO_NGUON[m.source], goi: maGoi };
}

/** Dòng này có giá trị đặt ở chính tầng đang xem (nên mới có gì để "Về mặc định"). */
export function datOTangNay(m: MucCaiDat, nhap: BanNhap, phamVi: PhamViCaiDat): boolean {
  if (m.key in nhap) return nhap[m.key] !== null;
  return m.source === phamVi;
}

// ---------------------------------------------------------------------------
// Kiểm giá trị phía giao diện
// ---------------------------------------------------------------------------

export type MaLoiGiaTri =
  | 'kieu_sai'
  | 'khong_phai_so_nguyen'
  | 'nho_hon_min'
  | 'lon_hon_max'
  | 'ngoai_danh_sach'
  | 'qua_so_dong'
  | 'cidr_sai'
  | 'cidr_trung'
  | 'cidr_tat_ca'
  /** Ô nhập theo dòng của danh sách IP còn dòng sai (bản nháp là chuỗi); `thamSo.so` là số dòng sai. */
  | 'dong_ip_sai'
  | 'thoi_diem_qua_khu'
  | 'thoi_diem_qua_xa'
  | 'email_sai'
  | 'qua_so_nguoi_nhan'
  | 'sai_dinh_dang'
  | 'thieu_kenh'
  | 'chuoi_rong'
  | 'chi_duoc_siet'
  | 'qua_so_quy_tac'
  /** Một quy tắc cảnh báo sai; `thamSo`: `dong`, `ten`, `truong`. */
  | 'quy_tac_sai';

export interface LoiGiaTri {
  ma: MaLoiGiaTri;
  /** Tham số cho câu dịch: `{{min}}`, `{{max}}`, `{{dong}}`… */
  thamSo?: Record<string, string | number>;
}

export interface NguCanhKiem {
  /** Mốc giờ hiện tại (ms) — cho `timestamp`. */
  bayGio: number;
  /** Giá trị kế thừa, cho luật "chỉ được siết". */
  keThua?: unknown;
  /** Người sửa chỉ được siết (Tenant Admin ở tầng tenant). */
  chiSiet?: boolean;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function kiemSo(
  n: unknown,
  nguyen: boolean,
  bien: BienHieuLuc | null,
): LoiGiaTri | null {
  if (typeof n !== 'number' || !Number.isFinite(n)) return { ma: 'kieu_sai' };
  if (nguyen && !Number.isInteger(n)) return { ma: 'khong_phai_so_nguyen' };
  if (bien?.min !== null && bien?.min !== undefined && n < bien.min) {
    return { ma: 'nho_hon_min', thamSo: { min: bien.min } };
  }
  if (bien?.max !== null && bien?.max !== undefined && n > bien.max) {
    return { ma: 'lon_hon_max', thamSo: { max: bien.max } };
  }
  return null;
}

/**
 * Kiểm một giá trị theo danh mục và biên hiệu lực.
 *
 * Đây là UX, không phải chốt chặn: `SettingsValidator` ở backend kiểm lại và
 * trả lỗi theo trường. Ở đây chỉ để người dùng biết trước khi bấm lưu.
 */
export function kiemGiaTri(
  def: SettingDefinition,
  bien: BienHieuLuc | null,
  giaTri: unknown,
  ngu: NguCanhKiem,
): LoiGiaTri | null {
  switch (def.type) {
    case 'boolean': {
      if (typeof giaTri !== 'boolean') return { ma: 'kieu_sai' };
      if (def.direction === 'TRUE_IS_STRICTER' && ngu.chiSiet && ngu.keThua === true && !giaTri) {
        return { ma: 'chi_duoc_siet' };
      }
      return null;
    }
    case 'integer':
    case 'number': {
      if (giaTri === null && def.default === null) return null;
      const loi = kiemSo(giaTri, def.type === 'integer', bien);
      if (loi) return loi;
      const keThua = ngu.keThua;
      if (ngu.chiSiet && typeof keThua === 'number') {
        const n = giaTri as number;
        if (def.direction === 'LOWER_IS_STRICTER' && n > keThua) {
          return { ma: 'chi_duoc_siet', thamSo: { gioiHan: keThua } };
        }
        if (def.direction === 'HIGHER_IS_STRICTER' && n < keThua) {
          return { ma: 'chi_duoc_siet', thamSo: { gioiHan: keThua } };
        }
      }
      return null;
    }
    case 'enum':
      return typeof giaTri === 'string' && (def.values ?? []).includes(giaTri)
        ? null
        : { ma: 'ngoai_danh_sach' };
    case 'timestamp': {
      if (giaTri === null) return null;
      if (typeof giaTri !== 'string') return { ma: 'kieu_sai' };
      const t = Date.parse(giaTri);
      if (Number.isNaN(t)) return { ma: 'kieu_sai' };
      if (t <= ngu.bayGio) return { ma: 'thoi_diem_qua_khu' };
      const toiDa = bien?.max_future_minutes ?? def.bounds?.max_future_minutes;
      if (toiDa !== null && toiDa !== undefined && t > ngu.bayGio + toiDa * 60_000) {
        return { ma: 'thoi_diem_qua_xa', thamSo: { phut: toiDa } };
      }
      return null;
    }
    case 'cidr_list': {
      // Ô nhập theo dòng (ADR-0098) giữ nguyên văn khi còn dòng sai; lỗi từng dòng hiện ngay dưới ô
      if (typeof giaTri === 'string') {
        const kq = docDanhSachIp(giaTri, {
          toiDa: bien?.max_items ?? undefined,
          camTatCa: Boolean(bien?.forbid_catch_all ?? def.bounds?.forbid_catch_all),
        });
        return kq.loi.length > 0 ? { ma: 'dong_ip_sai', thamSo: { so: kq.loi.length } } : null;
      }
      if (!Array.isArray(giaTri)) return { ma: 'kieu_sai' };
      const ds = giaTri as DongCidr[];
      if (bien?.max_items && ds.length > bien.max_items) {
        return { ma: 'qua_so_dong', thamSo: { max: bien.max_items } };
      }
      const daCo = new Set<string>();
      for (let i = 0; i < ds.length; i++) {
        const kq = chuanHoaCidr(ds[i]!.cidr);
        if (!kq.ok) return { ma: 'cidr_sai', thamSo: { dong: i + 1, cidr: ds[i]!.cidr } };
        if ((bien?.forbid_catch_all ?? def.bounds?.forbid_catch_all) && laTatCaDiaChi(kq.cidr)) {
          return { ma: 'cidr_tat_ca', thamSo: { dong: i + 1 } };
        }
        if (daCo.has(kq.cidr)) return { ma: 'cidr_trung', thamSo: { dong: i + 1, cidr: kq.cidr } };
        daCo.add(kq.cidr);
      }
      return null;
    }
    case 'notify_route': {
      const r = giaTri as TuyenThongBao | null;
      if (!r || !Array.isArray(r.channels) || !Array.isArray(r.recipients)) return { ma: 'kieu_sai' };
      if (r.channels.length === 0) return { ma: 'thieu_kenh' };
      const toiDaNguoi = bien?.max_items ?? def.bounds?.max_items;
      if (toiDaNguoi && r.recipients.length > toiDaNguoi) {
        return { ma: 'qua_so_nguoi_nhan', thamSo: { max: toiDaNguoi } };
      }
      const sai = r.recipients.find((e) => !EMAIL.test(e));
      return sai === undefined ? null : { ma: 'email_sai', thamSo: { email: sai } };
    }
    case 'integer_list': {
      if (!Array.isArray(giaTri)) return { ma: 'kieu_sai' };
      if (bien?.max_items && giaTri.length > bien.max_items) {
        return { ma: 'qua_so_dong', thamSo: { max: bien.max_items } };
      }
      for (const n of giaTri) {
        const loi = kiemSo(n, true, bien);
        if (loi) return loi;
      }
      return null;
    }
    case 'string_list': {
      if (!Array.isArray(giaTri)) return { ma: 'kieu_sai' };
      if (giaTri.some((s) => typeof s !== 'string' || !s.trim())) return { ma: 'chuoi_rong' };
      if (bien?.max_items && giaTri.length > bien.max_items) {
        return { ma: 'qua_so_dong', thamSo: { max: bien.max_items } };
      }
      const mau = bien?.item_pattern ?? def.bounds?.item_pattern;
      if (mau) {
        const re = new RegExp(mau);
        const sai = (giaTri as string[]).find((s) => !re.test(s));
        if (sai !== undefined) return { ma: 'sai_dinh_dang', thamSo: { giaTri: sai } };
      }
      return null;
    }
    case 'alert_rule_list': {
      const kq = kiemDanhSachQuyTac(giaTri, {
        max_items: bien?.max_items ?? def.bounds?.max_items,
        fields: bien?.fields ?? def.bounds?.fields,
      });
      if (!kq) return null;
      if (kq.ma === 'kieu_sai') return { ma: 'kieu_sai' };
      if (kq.ma === 'qua_so_quy_tac') return { ma: 'qua_so_quy_tac', thamSo: { max: kq.max } };
      return { ma: 'quy_tac_sai', thamSo: { dong: kq.chiSo + 1, ten: kq.ten, truong: kq.truong } };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Thay đổi có thể khoá người dùng
// ---------------------------------------------------------------------------

/**
 * Dải tin cậy không còn chặn ai (ADR-0098) nên đổi dải không còn là rủi ro tự khoá; kiểm tự khoá chuyển sang
 * blocklist (`docDanhSachIp` với `ipCuaBan`).
 */
export type MaRuiRo =
  /** Vừa bật đòi OTP ngoài dải tin cậy (ADR-0090 mục 2). */
  | 'mfa_ngoai_dai_bat'
  /** Bật bắt buộc MFA. */
  | 'mfa_bat'
  /** Tắt "chỉ nhận email thuộc domain đã xác minh": nhận email cá nhân, ép MFA cả tổ chức (ADR-0099). */
  | 'email_ngoai_domain';

export interface RuiRo {
  ma: MaRuiRo;
  /** Chặn lưu: hậu quả là tự khoá mình, không có đường tự gỡ. */
  chan: boolean;
}

/**
 * Nhận ra thay đổi có thể làm người dùng mất truy cập hoặc phải làm thêm bước (ADR-0059 mục 8).
 */
export function ruiRoKhoa(thayDoi: readonly ThayDoiNhap[]): RuiRo[] {
  const ra: RuiRo[] = [];

  const mfaNgoaiDai = thayDoi.find((d) => d.key === 'network.mfa_ngoai_dai');
  if (mfaNgoaiDai && mfaNgoaiDai.truoc !== true && mfaNgoaiDai.sau === true) {
    ra.push({ ma: 'mfa_ngoai_dai_bat', chan: false });
  }

  const mfa = thayDoi.find((d) => d.key === 'auth.mfa_required');
  if (mfa && mfa.truoc !== true && mfa.sau === true) ra.push({ ma: 'mfa_bat', chan: false });

  const email = thayDoi.find((d) => d.key === KHOA_EMAIL_DOMAIN);
  if (email && email.truoc !== false && email.sau === false) ra.push({ ma: 'email_ngoai_domain', chan: false });

  return ra;
}

/** `auth.chi_email_domain_xac_minh` (ADR-0099). */
export const KHOA_EMAIL_DOMAIN = 'auth.chi_email_domain_xac_minh';

/**
 * Lần lưu này làm MFA hiệu lực chuyển sang bật — bật `auth.mfa_required`, hoặc tắt chính sách domain email (ép MFA,
 * ADR-0099) — nên hộp xác nhận hỏi áp dụng ngay hay từ lần đăng nhập sau.
 */
export function coBatMfa(ruiRo: readonly RuiRo[]): boolean {
  return ruiRo.some((r) => r.ma === 'mfa_bat' || r.ma === 'email_ngoai_domain');
}
